import { useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
  type TooltipValueType,
} from 'recharts';
import { formatCompact, formatDay, formatNumber } from '../lib/format';
import { Button } from './ui';

export interface TrendPoint {
  date: string;
  new_users: number;
  new_subscriptions: number;
}

/**
 * Two series over time, so lines (change over time, identity by hue).
 *
 * Both series are counts of the same kind of thing — devices per day — so they
 * share one y-axis. A second scale for subscriptions would exaggerate a small
 * number into a line that visually rivals registrations, which is the single
 * most misleading thing a chart like this can do.
 */
const SERIES = [
  { key: 'new_users', label: 'New devices', color: 'var(--series-1)' },
  {
    key: 'new_subscriptions',
    label: 'Purchases & renewals',
    color: 'var(--series-2)',
  },
] as const;

export function TrendChart({ data }: { data: TrendPoint[] }) {
  const [showTable, setShowTable] = useState(false);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Legend />
        <Button
          variant="ghost"
          className="text-xs"
          onClick={() => setShowTable((current) => !current)}
          aria-expanded={showTable}
        >
          {showTable ? 'Show chart' : 'Show data'}
        </Button>
      </div>

      {showTable ? (
        <DataTable data={data} />
      ) : (
        <div className="h-64 w-full sm:h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={data}
              margin={{ top: 8, right: 12, bottom: 0, left: -18 }}
            >
              {/* Recessive chrome: horizontal hairlines only, no vertical rules. */}
              <CartesianGrid
                horizontal
                vertical={false}
                stroke="var(--grid)"
                strokeWidth={1}
              />
              <XAxis
                dataKey="date"
                tickFormatter={formatDay}
                tickLine={false}
                axisLine={{ stroke: 'var(--line)' }}
                tick={{ fill: 'var(--ink-3)', fontSize: 11 }}
                minTickGap={24}
              />
              <YAxis
                tickFormatter={formatCompact}
                tickLine={false}
                axisLine={false}
                tick={{ fill: 'var(--ink-3)', fontSize: 11 }}
                width={56}
                allowDecimals={false}
              />
              <Tooltip
                content={ChartTooltip}
                cursor={{ stroke: 'var(--line-strong)', strokeWidth: 1 }}
              />
              {SERIES.map((series) => (
                <Line
                  key={series.key}
                  type="monotone"
                  dataKey={series.key}
                  name={series.label}
                  stroke={series.color}
                  strokeWidth={2}
                  dot={false}
                  // Comfortably larger than the line, with a surface ring so
                  // the two series stay separable where they cross.
                  activeDot={{
                    r: 4.5,
                    strokeWidth: 2,
                    stroke: 'var(--surface)',
                  }}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

/**
 * Identity is never colour alone: the swatch is paired with a text label, in
 * ink tokens rather than the series colour.
 */
function Legend() {
  return (
    <ul className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
      {SERIES.map((series) => (
        <li key={series.key} className="flex items-center gap-1.5 text-xs">
          <span
            aria-hidden
            className="h-0.5 w-4 rounded-full"
            style={{ backgroundColor: series.color }}
          />
          <span className="text-ink-2">{series.label}</span>
        </li>
      ))}
    </ul>
  );
}

function ChartTooltip({
  active,
  payload,
  label,
  // Recharts' own generics: narrowing either one makes the component
  // unassignable to `<Tooltip content>`.
}: TooltipContentProps<TooltipValueType, string | number>) {
  if (!active || !payload?.length) return null;

  return (
    <div className="rounded-lg border border-line bg-surface px-3 py-2 shadow-lg">
      <p className="mb-1.5 text-xs font-medium text-ink">
        {formatDay(String(label))}
      </p>
      <ul className="space-y-1">
        {payload.map((entry) => (
          <li
            key={String(entry.dataKey)}
            className="flex items-center gap-2 text-xs whitespace-nowrap"
          >
            <span
              aria-hidden
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-ink-2">{entry.name}</span>
            <span className="tnum ml-auto font-medium text-ink">
              {formatNumber(Number(entry.value ?? 0))}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The same numbers as a table — the accessible fallback for the chart. */
function DataTable({ data }: { data: TrendPoint[] }) {
  return (
    <div className="max-h-72 overflow-auto rounded-lg border border-line">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-raised text-xs text-ink-2">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Date</th>
            {SERIES.map((series) => (
              <th key={series.key} className="px-3 py-2 text-right font-medium">
                {series.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {data.map((point) => (
            <tr key={point.date}>
              <td className="px-3 py-1.5 text-ink-2">{formatDay(point.date)}</td>
              <td className="px-3 py-1.5 text-right text-ink">
                {formatNumber(point.new_users)}
              </td>
              <td className="px-3 py-1.5 text-right text-ink">
                {formatNumber(point.new_subscriptions)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
